import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
} from 'firebase/firestore'
import { db } from '@/services/firebase'
import {
  getGlobalCollection,
  getInstituteCollection,
  resolveScopedCollection,
  resolveScopedDocRef,
} from '@/services/firestorePaths'
import { omitUndefined } from '@/utils/objectUtils'
import type {
  Unit,
  UnitCreatePayload,
  UnitUpdatePayload,
} from '@/types'

/**
 * @auto-generated — DESCRIBE: What values this type allows and in which contexts it is used.
 */
export type UnitWithScope = Unit & { isGlobal: boolean }

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}

/**
 * Fetch a single unit by id and scope.
 * If scope is not provided, searches global first, then all institutes (slower).
 */
export async function getUnit(id: string, scope?: string | 'global'): Promise<UnitWithScope | null> {
  try {
    if (scope) {
      const ref = resolveScopedDocRef('units', id, scope)
      const snap = await getDoc(ref)
      if (!snap.exists()) return null
      return {
        id: snap.id,
        ...snap.data(),
        isGlobal: scope === 'global'
      } as UnitWithScope
    }
    
    // Try global first
    const globalRef = resolveScopedDocRef('units', id, 'global')
    const globalSnap = await getDoc(globalRef)
    if (globalSnap.exists()) {
      return {
        id: globalSnap.id,
        ...globalSnap.data(),
        isGlobal: true
      } as UnitWithScope
    }
    
    // Fallback: search all institutes (expensive, prefer passing scope)
    const institutesSnap = await getDocs(collection(db, 'institutes'))
    for (const instDoc of institutesSnap.docs) {
      const instRef = resolveScopedDocRef('units', id, instDoc.id)
      const instSnap = await getDoc(instRef)
      if (instSnap.exists()) {
        return {
          id: instSnap.id,
          ...instSnap.data(),
          isGlobal: false
        } as UnitWithScope
      }
    }
    
    return null
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List all units (deprecated - use listUnitsByInstitute for better performance).
 */
export async function listUnits(): Promise<UnitWithScope[]> {
  try {
    const institutesSnap = await getDocs(collection(db, 'institutes'))
    const [globalSnap, ...instituteSnaps] = await Promise.all([
      getDocs(getGlobalCollection('units')),
      ...institutesSnap.docs.map((instDoc) =>
        getDocs(getInstituteCollection('units', instDoc.id))
      )
    ])
    
    const globalUnits = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as UnitWithScope))
    
    const instituteUnits = instituteSnaps.flatMap((snap) =>
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isGlobal: false
      } as UnitWithScope))
    )
    
    return [...globalUnits, ...instituteUnits]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List units by institute with global units included (parallel query pattern).
 * This is the recommended function for optimal performance.
 */
export async function listUnitsByInstitute(instituteId: string): Promise<UnitWithScope[]> {
  try {
    const [globalSnap, instituteSnap] = await Promise.all([
      getDocs(getGlobalCollection('units')),
      getDocs(getInstituteCollection('units', instituteId))
    ])
    
    const globalUnits = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as UnitWithScope))
    
    const instituteUnits = instituteSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: false
    } as UnitWithScope))
    
    return [...globalUnits, ...instituteUnits]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List units by multiple institutes (includes global).
 */
export async function listUnitsByInstitutes(institutes: string[]): Promise<UnitWithScope[]> {
  if (institutes.length === 0) return []
  try {
    const [globalSnap, ...instituteSnaps] = await Promise.all([
      getDocs(getGlobalCollection('units')),
      ...institutes.map((instituteId) => getDocs(getInstituteCollection('units', instituteId)))
    ])
    
    const globalUnits = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as UnitWithScope))
    
    const instituteUnits = instituteSnaps.flatMap((snap) =>
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isGlobal: false
      } as UnitWithScope))
    )
    
    return [...globalUnits, ...instituteUnits]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List units linked to a specific institute via Institute.unitIds.
 * Fetches the institute document and then retrieves each unit individually.
 */
export async function listUnitsByInstituteId(instituteId: string): Promise<UnitWithScope[]> {
  try {
    const instSnap = await getDoc(doc(db, 'institutes', instituteId))
    if (!instSnap.exists()) return []
    const unitIds: string[] = (instSnap.data() as { unitIds?: string[] }).unitIds ?? []
    if (unitIds.length === 0) return []
    const unitDocs = await Promise.all(
      unitIds.map((uid) => getUnit(uid, instituteId))
    )
    return unitDocs.filter(Boolean) as UnitWithScope[]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List child units of a given parent unit.
 */
export async function listChildUnits(parentUnitId: string, instituteId: string): Promise<UnitWithScope[]> {
  try {
    const allUnits = await listUnitsByInstitute(instituteId)
    return allUnits.filter((unit) => unit.parentUnitId === parentUnitId)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Create a new unit in the specified scope.
 * @param payload - Unit data
 * @param scope - 'global' (superuser only) or institute ID
 */
export async function createUnit(
  payload: Omit<UnitCreatePayload, 'ownedBy'>,
  scope: string | 'global'
): Promise<string> {
  if (!scope) {
    throw new Error('[unit.service] scope is required')
  }
  try {
    // Remove ownedBy if present
    const { ownedBy: _, ...cleanPayload } = payload as any
    
    const ref = await addDoc(resolveScopedCollection('units', scope), {
      ...omitUndefined(cleanPayload),
      instituteId: scope === 'global' ? undefined : scope,
      createdAt: serverTimestamp(),
    })
    return ref.id
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Update a unit in the specified scope.
 * @param id - Unit ID
 * @param scope - 'global' or institute ID
 * @param payload - Fields to update
 */
export async function updateUnit(
  id: string,
  scope: string | 'global',
  payload: Omit<UnitUpdatePayload, 'ownedBy'>
): Promise<void> {
  try {
    // Remove ownedBy if present
    const { ownedBy: _, ...cleanPayload } = payload as any
    
    await updateDoc(resolveScopedDocRef('units', id, scope), {
      ...cleanPayload,
      ...('description' in cleanPayload && cleanPayload.description === undefined ? { description: deleteField() } : {}),
      ...('archived' in cleanPayload && cleanPayload.archived === undefined ? { archived: deleteField() } : {}),
      ...('unitConstraints' in cleanPayload && cleanPayload.unitConstraints === undefined ? { unitConstraints: deleteField() } : {}),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Delete a unit from the specified scope.
 * @param id - Unit ID
 * @param scope - 'global' or institute ID
 */
export async function deleteUnit(id: string, scope: string | 'global'): Promise<void> {
  try {
    await deleteDoc(resolveScopedDocRef('units', id, scope))
  } catch (error) {
    throw createServiceError(error)
  }
}

/** Associate a child unit with a parent unit (bidirectional) */
export async function addChildUnitToUnit(
  parentId: string,
  childId: string,
  parentScope: string | 'global',
  childScope: string | 'global'
): Promise<void> {
  try {
    const parentRef = resolveScopedDocRef('units', parentId, parentScope)
    const childRef = resolveScopedDocRef('units', childId, childScope)
    await Promise.all([
      updateDoc(parentRef, { childUnitIds: arrayUnion(childId) }),
      updateDoc(childRef, { parentUnitId: parentId }),
    ])
  } catch (error) {
    throw createServiceError(error)
  }
}

/** Remove child unit association from parent (bidirectional) */
export async function removeChildUnitFromUnit(
  parentId: string,
  childId: string,
  parentScope: string | 'global',
  childScope: string | 'global'
): Promise<void> {
  try {
    const parentRef = resolveScopedDocRef('units', parentId, parentScope)
    const childRef = resolveScopedDocRef('units', childId, childScope)
    await Promise.all([
      updateDoc(parentRef, { childUnitIds: arrayRemove(childId) }),
      updateDoc(childRef, { parentUnitId: null }),
    ])
  } catch (error) {
    throw createServiceError(error)
  }
}
